'use memo'

import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import type {
  OrgRegistrationListRow,
  OrgRegistrationSummaryRow,
} from '@sportninja/common/features/organization/registration/utils/listRows'
import {
  compareOrgRegistrationSortValues,
  getOrgRegistrationSortValue,
  type OrgRegistrationListSortKey,
  type OrgRegistrationSortValue,
} from '@sportninja/common/features/organization/registration/utils/listSort'
import { getRegistrationSportName } from '@sportninja/common/features/organization/registration/utils/sportArt'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import RowExpandToggle from 'src/pages/registration/components/DivisionGrid/cells/RowExpandToggle'
import OrgRegistrationStatusChip from '../components/OrgRegistrationStatusChip'
import ClosesInCell from '../components/list/cells/ClosesInCell'
import CompetitionCell from '../components/list/cells/CompetitionCell'
import DateLinesCell from '../components/list/cells/DateLinesCell'
import LocationCell from '../components/list/cells/LocationCell'
import RegisterCell from '../components/list/cells/RegisterCell'
import SportCell from '../components/list/cells/SportCell'

type Column = ColDef<OrgRegistrationListRow>
type Params = ICellRendererParams<OrgRegistrationListRow>

/** A summary row's data, or null on a detail row (which has no cells). */
const summaryOf = ({ data }: Params): OrgRegistrationSummaryRow | null =>
  data?.kind === 'registration' ? data : null

/** A sortable column, sorted by `key` — blanks last in both directions. */
const sortedBy = (key: OrgRegistrationListSortKey): Column => ({
  valueGetter: ({ data }) => getOrgRegistrationSortValue(data, key),
  comparator: (
    a: OrgRegistrationSortValue,
    b: OrgRegistrationSortValue,
    _nodeA,
    _nodeB,
    isDescending
  ) => compareOrgRegistrationSortValues(a, b, isDescending),
})

interface Options {
  onToggle: (registrationId: string) => void
  onOpen: (registration: OrgRegistration) => void
  /**
   * A phone-width screen: only the chevron and Register stay pinned. Pinning
   * the competition too would leave the rest of the table no room to scroll in
   * (AG Grid then unpins columns on its own, out of order).
   */
  isCompact: boolean
}

/**
 * The list view's columns: the expand chevron and the competition pinned on
 * the left (on a phone only the chevron), then sport, location, status, the
 * registration window, the season start and the countdown scrolling under
 * them, and the Register action pinned on the right so it is always in reach.
 * Every column but the chevron and Register sorts. Minimum widths fit each
 * column's content, so a narrow screen scrolls the table sideways instead of
 * clipping a cell.
 */
export const useRegistrationListColumns = ({
  onToggle,
  onOpen,
  isCompact,
}: Options): Column[] => {
  'use memo'

  // 'Competition' on SportNinja, 'Season' on Canlan.
  const competition = t('common:competition', {})

  return [
    {
      colId: 'expand',
      headerName: '',
      width: 48,
      pinned: 'left',
      sortable: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        // Nothing to open without divisions — the card drops its toggle too.
        if (!row || row.registration.divisions.length === 0) {
          return null
        }
        return (
          <RowExpandToggle
            expanded={row.isExpanded}
            onToggle={() => onToggle(row.id)}
            label={capitalizeWords(
              row.isExpanded ? 'Hide divisions' : 'Show divisions'
            )}
          />
        )
      },
    },
    {
      ...sortedBy('name'),
      colId: 'name',
      headerName: capitalizeWords(competition),
      width: isCompact ? 200 : 260,
      pinned: isCompact ? null : 'left',
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? <CompetitionCell registration={row.registration} /> : null
      },
    },
    {
      ...sortedBy('sport'),
      colId: 'sport',
      headerName: capitalizeWords('Sport'),
      minWidth: 150,
      flex: 1,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? (
          <SportCell sportName={getRegistrationSportName(row.registration)} />
        ) : null
      },
    },
    {
      ...sortedBy('location'),
      colId: 'location',
      headerName: capitalizeWords('Location'),
      minWidth: 180,
      flex: 1.2,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? <LocationCell venues={row.registration.venues} /> : null
      },
    },
    {
      ...sortedBy('status'),
      colId: 'status',
      headerName: capitalizeWords('Status'),
      minWidth: 150,
      flex: 1,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? (
          <OrgRegistrationStatusChip
            badge={row.model.badge}
            label={row.model.badgeLabel}
          />
        ) : null
      },
    },
    {
      ...sortedBy('window'),
      colId: 'window',
      headerName: capitalizeWords('Registration'),
      minWidth: 150,
      flex: 1,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? (
          <DateLinesCell first={row.windowStart} second={row.windowEnd} />
        ) : null
      },
    },
    {
      ...sortedBy('season'),
      colId: 'season',
      headerName: capitalizeWords(`${competition} start`),
      minWidth: 200,
      flex: 1,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? <DateLinesCell first={row.seasonStart} /> : null
      },
    },
    {
      ...sortedBy('countdown'),
      colId: 'countdown',
      headerName: capitalizeWords('Closes in'),
      minWidth: 170,
      flex: 1,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? <ClosesInCell model={row.model} /> : null
      },
    },
    {
      colId: 'register',
      headerName: capitalizeWords('Register'),
      minWidth: 150,
      width: 150,
      pinned: 'right',
      sortable: false,
      resizable: false,
      cellRenderer: (params: Params) => {
        const row = summaryOf(params)
        return row ? (
          <RegisterCell
            badge={row.model.badge}
            onOpen={() => onOpen(row.registration)}
          />
        ) : null
      },
    },
  ]
}
